import { Sparkles, AlertTriangle, BookOpen, Calendar, ShieldAlert } from "lucide-react";
import { StatusBadge, PriorityBadge, TagList, Avatar, Badge, relTime } from "@/components/library/badges";

// Specialised entity cards — all share badges/avatars, differ in emphasis.
export function TaskCard({ task, onOpen, testid }) {
  return (
    <button onClick={() => onOpen?.(task)} data-testid={testid || `task-card-${task.id}`}
      className="text-left w-full bg-card border border-border rounded-2xl p-5 hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display font-semibold tracking-tight">{task.title}</h3>
        {task.status && <StatusBadge entityType="task" status={task.status} />}
      </div>
      <div className="flex items-center flex-wrap gap-2 mt-4">
        {task.priority && <PriorityBadge priority={task.priority} />}
        {task.assignee && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Avatar name={task.assignee.name} size={20} />{task.assignee.name}</span>}
        {task.due_at && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="h-3 w-3" />{relTime(task.due_at)}</span>}
      </div>
      {task.tags?.length > 0 && <div className="mt-3"><TagList tags={task.tags} /></div>}
    </button>
  );
}

export function ManualCard({ manual, onOpen, testid }) {
  return (
    <button onClick={() => onOpen?.(manual)} data-testid={testid || `manual-card-${manual.id}`}
      className="text-left w-full bg-card border border-border rounded-2xl p-5 hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-4 w-4 text-[#C58E1A]" />
        <Badge tone="accent">{manual.category}</Badge>
      </div>
      <h3 className="font-display font-semibold tracking-tight">{manual.title}</h3>
      <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{manual.content}</p>
      <p className="text-xs text-muted-foreground mt-4 font-mono">{manual.author} · {relTime(manual.updated_at)}</p>
    </button>
  );
}

export function IncidentCard({ incident, onOpen, testid }) {
  return (
    <button onClick={() => onOpen?.(incident)} data-testid={testid || `incident-card-${incident.id}`}
      className="text-left w-full bg-card border border-border rounded-2xl p-5 hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 text-[hsl(0_80%_72%)] shrink-0" />
          <h3 className="font-display font-semibold tracking-tight truncate">{incident.title}</h3>
        </div>
        <StatusBadge entityType="incident" status={incident.status} />
      </div>
      {incident.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{incident.description}</p>}
      <div className="flex items-center gap-2 mt-4">
        <PriorityBadge priority={incident.severity} />
        <span className="text-xs text-muted-foreground">{incident.reporter} · {relTime(incident.updated_at)}</span>
      </div>
    </button>
  );
}

const RISK = { hög: "danger", medel: "warning", låg: "success", okänd: "neutral" };
export function AIAnalysisCard({ analysis, testid = "ai-analysis-card" }) {
  const r = analysis.result || {};
  return (
    <div data-testid={testid} className="bg-card border border-accent/30 rounded-2xl p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 grid place-items-center rounded-lg bg-accent text-white"><Sparkles className="h-4 w-4" /></span>
          <span className="font-display font-semibold text-sm">AI-analys</span>
          {analysis.mode && <Badge tone="accent">{analysis.mode === "auto" ? "Automatisk" : "Manuell"}</Badge>}
        </div>
        {r.risk && <Badge tone={RISK[r.risk] || "neutral"}><ShieldAlert className="h-3 w-3" />Risk: {r.risk}</Badge>}
      </div>
      <p className="text-sm leading-relaxed">{r.analysis}</p>
      {r.recommended_actions?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {r.recommended_actions.map((a, i) => (
            <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-accent">→</span>{a}</li>
          ))}
        </ul>
      )}
      <p className="text-[0.7rem] text-muted-foreground mt-4 font-mono">
        {analysis.model} · prompt {analysis.prompt_version}
        {analysis.confidence != null && ` · säkerhet ${Math.round(analysis.confidence * 100)}%`} · {relTime(analysis.created_at)}
      </p>
    </div>
  );
}
